import { TitleIdToSelf } from './../../reducers/self'
import { createSelector } from '../selectorWrapper'
import {
  notificationAlertsSelector,
  notificationWarningsSelector
} from '../notificationFragment'
import {
  svodWinnerChildrenSelector,
  svodProblemsMessagesSelector,
  svodMetadataSelector,
  svodMetadataBehaviourSelector,
  tvodMessagesSelector,
  tvodMetadataSelector,
  tvodMetadataMessagesSelector,
  acceptedPreorderSelector,
  playbackMetadataSelector,
  playbackChildrenSelector,
  acquisitionActionsSelector,
  disneyActionSelector,
  mwtwSelector,
  tvodWinnerChildrenSelector,
  playbackFirstChildSelector,
  downloadFirstChildSelector,
  notificationActionsSelector,
} from '../actionFragment'
import { pageSelectors, simpleSelectors } from '../basicState'
import { isIosMShopSelector } from '../pageContext'
import {
  sessionIdSelector,
  domainSelector,
  sanitizedRequestContextSelector,
  IRequestContext,
} from '../requestContext'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { isRetail } from '../../utils/domainUtils'
import { Domain } from 'DVWebNodeUtils'
import { buildDvString, convertToDVString } from '../../utils/stringUtils'
import { playerConfigDataSelector } from '../player'
import { playerConfig } from '@shared/player'
import { isLiveEntityType } from '../../utils/entityTypes'

export type LogoMessage = {
  text: WpsRuntime.atv.wps.DVString
  logo: string
}

export type TvodSvodMessagesData = {
  subProblemMessages?: WpsRuntime.atv.wps.DVString[]
  offerMessages?: WpsRuntime.atv.wps.DVStringList
  topLevelTvodMessages?: WpsRuntime.atv.wps.DVStringList
  acceptedPreorder?: WpsRuntime.atv.wps.AcceptedPreorderActionProps
}
export const tvodSvodMessagesDataSelector = createSelector(
  [
    svodProblemsMessagesSelector,
    tvodMessagesSelector,
    tvodMetadataMessagesSelector,
    acceptedPreorderSelector,
  ],
  (
    svodProblemMessages?: WpsRuntime.atv.wps.DVString[],
    tvodWinnerMessages?: WpsRuntime.atv.wps.DVStringList,
    tvodMetadataMessages?: WpsRuntime.atv.wps.DVStringList,
    acceptedPreorder?: WpsRuntime.atv.wps.AcceptedPreorderActionProps
  ) =>
    ({
      subProblemMessages: svodProblemMessages,
      offerMessages: tvodWinnerMessages,
      topLevelTvodMessages: tvodMetadataMessages,
      acceptedPreorder,
    } as TvodSvodMessagesData)
)

export const svodActionsLogoMessagesSelector = createSelector(
  svodWinnerChildrenSelector,
  (svods?: WpsRuntime.atv.wps.SubscribeAction[]) =>
    (svods &&
      (svods
        .map(
          svod =>
            svod.message && {
              text: convertToDVString(svod.message) as WpsRuntime.atv.wps.DVString,
              logo: svod.image,
            }
        )
        .filter(Boolean) as LogoMessage[])) ||
    ([] as LogoMessage[])
)

export const svodMetadataLogoMessageSelector = createSelector(
  svodMetadataSelector,
  (svodMetadata?: WpsRuntime.atv.wps.ActionMetadataProps) =>
    (svodMetadata &&
      svodMetadata.label &&
      ({
        text: convertToDVString(svodMetadata.label),
        logo: svodMetadata.imageLink,
      } as LogoMessage)) ||
    undefined
)

export const notificationActionsLogoMessagesSelector = createSelector(
  notificationActionsSelector,
  (notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[]) =>
    (notificationActions &&
      (notificationActions
        .map(notificationAction => ({
          text: convertToDVString(notificationAction.header),
          logo: notificationAction.logo,
        }))
        .filter(Boolean) as LogoMessage[])) ||
    ([] as LogoMessage[])
)

export const notificationActionsTextBlockMessagesSelector = createSelector(
  notificationActionsSelector,
  (notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[]) => ({
    messages: (notificationActions || ([] as WpsRuntime.atv.wps.NotificationActionProps[]))
      .reduce((arr: WpsRuntime.atv.wps.DVStringList, notification) => {
        const message = convertToDVString(notification.message)
        if (
          (!notification.severity || ['CRITICAL', 'ERROR'].includes(notification.severity)) && message.string &&
          !arr.some(item => item.string === message.string)
        ) {
          arr.push(message)
        }
        return arr;
      }, [])
  })
)

export const notificationActionLeavingSoonMessagesSelector = createSelector(
  notificationActionsSelector,
  (notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[]) =>
    (
      notificationActions ||
      ([] as WpsRuntime.atv.wps.NotificationActionProps[])
    )
      .filter(
        ({ severity, messageSource }) =>
          severity === 'RELEVANT' && messageSource === 'LEAVING_SOON'
      )
      .map(notification => convertToDVString(notification.message))
)

export const notificationActionComingSoonMessagesSelector = createSelector(
  notificationActionsSelector,
  (notificationActions?: WpsRuntime.atv.wps.NotificationActionProps[]) =>
    (
      notificationActions ||
      ([] as WpsRuntime.atv.wps.NotificationActionProps[])
    )
      .filter(
        ({ severity, messageSource }) =>
          severity === 'RELEVANT' && messageSource === 'COMING_SOON'
      )

      .map(notification => convertToDVString(notification.message))
)

export const playbackMetadataLogoMessageSelector = createSelector(
  [playbackChildrenSelector, playbackMetadataSelector],
  (
    actions?: WpsRuntime.atv.wps.PlaybackActionList,
    metadata?: WpsRuntime.atv.wps.ActionMetadataProps
  ) => {
    const logo = metadata && metadata.imageLink
    const message =
      metadata &&
      metadata.messages &&
      metadata.messages[0] &&
      metadata.messages[0].string
    const freeActions =
      (actions &&
        actions.filter(action => action.entitlementType === 'FREE')) ||
      []
    return (
      (message &&
        freeActions.length === 0 &&
        logo &&
        ({
          text: convertToDVString(message),
          logo: logo,
        } as LogoMessage)) ||
      undefined
    )
  }
)

export const playbackActionsLogoMessageSelector = createSelector(
  playbackChildrenSelector,
  (actions?: WpsRuntime.atv.wps.PlaybackActionList) =>
    (actions &&
      (actions
        .map(
          action =>
            action.entitlementType !== 'FREE' &&
            action.heading &&
            action.logo && {
              text: convertToDVString(action.heading),
              logo: action.logo,
            }
        )
        .filter(Boolean) as LogoMessage[])) ||
    undefined
)

export const logoMessagesSelector = createSelector(
  [
    svodActionsLogoMessagesSelector,
    notificationActionsLogoMessagesSelector,
    playbackActionsLogoMessageSelector,
    playbackMetadataLogoMessageSelector,
    svodMetadataLogoMessageSelector,
    svodMetadataBehaviourSelector,
  ],
  (
    svodActionsLogoMessages: LogoMessage[],
    notificationActionLogoMessages: LogoMessage[],
    playbackActionLogoMessage?: LogoMessage[],
    playbackMetadataLogoMessage?: LogoMessage,
    svodMetadataLogoMessage?: LogoMessage,
    treeBehaviour?: WpsRuntime.atv.wps.TreeBehaviour
  ) => {
    const svodMessages =
      treeBehaviour && treeBehaviour === 'Collapsed'
        ? []
        : [svodMetadataLogoMessage, ...svodActionsLogoMessages]
    return [
      ...(playbackActionLogoMessage || []),
      playbackMetadataLogoMessage,
      ...notificationActionLogoMessages,
      ...svodMessages,
    ].filter(message => Boolean(message && message.text.string)) as LogoMessage[]
  }
)

export const playbackMetadataInfoMessageSelector = createSelector(
  [playbackChildrenSelector, playbackMetadataSelector],
  (
    actions?: WpsRuntime.atv.wps.PlaybackActionList,
    metadata?: WpsRuntime.atv.wps.ActionMetadataProps
  ) => {
    const logo = metadata && metadata.imageLink
    const messages = metadata && metadata.messages
    const freeActions =
      (actions &&
        actions.filter(action => action.entitlementType === 'FREE')) ||
      []

    return (freeActions.length === 0 && !logo && messages) || undefined
  }
)

export const playbackActionsInfoMessageSelector = createSelector(
  playbackChildrenSelector,
  (actions?: WpsRuntime.atv.wps.PlaybackActionList) => {
    return (
      (actions &&
        (actions
          .map(
            action =>
              action.entitlementType !== 'FREE' &&
              !action.logo &&
              action.heading &&
              buildDvString(action.heading)
          )
          .filter(Boolean) as WpsRuntime.atv.wps.DVStringList)) ||
      undefined
    )
  }
)

export const playbackMessagingComponentSelector = createSelector(
  [
    tvodSvodMessagesDataSelector,
    notificationActionLeavingSoonMessagesSelector,
    notificationActionComingSoonMessagesSelector,
    notificationWarningsSelector,
    logoMessagesSelector,
    pageSelectors.restriction,
    playbackMetadataInfoMessageSelector,
    playbackActionsInfoMessageSelector,
    notificationAlertsSelector,
    pageSelectors.headerDetail,
  ],
  (
    tvodSvodMessages: TvodSvodMessagesData,
    notificationLeavingSoonMessages: WpsRuntime.atv.wps.DVStringList,
    notificationComingSoonMessages: WpsRuntime.atv.wps.DVStringList,
    notificationWarnings?: WpsRuntime.atv.wps.WarningNotification[],
    logoMessages?: LogoMessage[],
    restrictionsFragment?: WpsRuntime.atv.wps.RestrictionFragmentModelProps,
    playbackMetadataInfoMessages?: WpsRuntime.atv.wps.DVStringList,
    playbackActionsInfoMessages?: WpsRuntime.atv.wps.DVStringList,
    notificationAlerts?:  WpsRuntime.atv.wps.AlertNotification[],
    detailFragment?: WpsRuntime.atv.wps.DetailFragmentModelProps,
  ) => {

    const notificationAlertDefaultMessages = notificationAlerts && notificationAlerts
      .filter(alertMessage => alertMessage.notificationType === 'Default')
      .map(_ => buildDvString(_.message) as WpsRuntime.atv.wps.DVString)

    // We do not show our ComingSoon message for Gen4 Live Events
    const notificationAlertComingSoonMessages =
      detailFragment && !isLiveEntityType(detailFragment.entityType) && notificationAlerts && notificationAlerts
      .filter(alertMessage => alertMessage.notificationType === 'ComingSoon')
      .map(_ => buildDvString(_.message) as WpsRuntime.atv.wps.DVString)

    // If we do not receive a Coming Soon message from TAPS then we fallback to our Coming Soon messages
    const comingSoonMessages = notificationComingSoonMessages.length === 0 ?
      (notificationAlertComingSoonMessages || []) : notificationComingSoonMessages

    const infoAlertMessages = [
      ...(tvodSvodMessages.offerMessages || []),
      ...(playbackMetadataInfoMessages || []),
      ...(playbackActionsInfoMessages || []),
      ...(notificationAlertDefaultMessages || []),
      ...comingSoonMessages,
    ].filter(Boolean)

    const notificationWarningMessages = [
      ...(notificationWarnings || []).map(_ => buildDvString(_.message) as WpsRuntime.atv.wps.DVString),
      ...notificationLeavingSoonMessages
    ]

    return {
      notificationWarnings: notificationWarningMessages,
      subProblemMessages: tvodSvodMessages.subProblemMessages,
      messagesWithLogo: logoMessages,
      infoAlertMessages: infoAlertMessages,
      topLevelTvodMessages: tvodSvodMessages.topLevelTvodMessages,
      acceptedPreorder: tvodSvodMessages.acceptedPreorder,
      maturityLockText:
        restrictionsFragment && restrictionsFragment.maturityLockText,
    }
  }
)

export const ageVerificationSelector = createSelector(
  pageSelectors.restriction,
  (restrictionsFragment?: WpsRuntime.atv.wps.RestrictionFragmentModelProps) =>
    restrictionsFragment &&
    restrictionsFragment.ageVerificationMessage && {
      message:
        restrictionsFragment && restrictionsFragment.ageVerificationMessage,
    }

)

export const singlePlaybackComponentSelector = createSelector(
  [
    sessionIdSelector,
    simpleSelectors.pageTitleId,
    playerConfigDataSelector,
    playbackFirstChildSelector,
    domainSelector,
  ],
  (
    sessionId: string,
    pageTitleId: string,
    playerConfigData: playerConfig.PlayerConfigData,
    playbackData?: WpsRuntime.atv.wps.PlaybackActionDataProps,
    domain?: Domain
  ) =>
    (playbackData && {
      playbackData,
      sessionId,
      pageTitleId,
      playerConfigData,
      isRetail: domain && isRetail(domain)
    }) ||
    undefined
)

export const downloadComponentSelector = createSelector(
  [downloadFirstChildSelector, simpleSelectors.self],
  (downloadData?: WpsRuntime.atv.wps.DownloadActionDataProps,
  selfFragments?: TitleIdToSelf
  ) => {
    const self = selfFragments && downloadData && selfFragments[downloadData.titleID]
    const dd = downloadData && {
      label: downloadData.label,
      downloadURL: downloadData.downloadURL,
      fallbackURL: downloadData.fallbackURL,
      titleID: downloadData.titleID,
      episodeNumber: (self && self.titleType.toLocaleLowerCase() === 'episode') ? self.sequenceNumber : undefined
    }
    return dd
  }
)

export type MultiplePlaybackData = {
  playbackDataList: WpsRuntime.atv.wps.PlaybackActionList
  playerConfigData: playerConfig.PlayerConfigData
  isRetail?: boolean
}

export const multiplePlaybackComponentSelector = createSelector(
  [
    sessionIdSelector,
    simpleSelectors.pageTitleId,
    playerConfigDataSelector,
    playbackChildrenSelector,
    domainSelector,
  ],
  (
    sessionId: string,
    pageTitleId: string,
    playerConfigData: playerConfig.PlayerConfigData,
    playbackDataList?: WpsRuntime.atv.wps.PlaybackActionList,
    domain?: Domain
  ) =>
    (playbackDataList &&
      playbackDataList.length > 0 &&
      ({
        playbackDataList,
        sessionId,
        pageTitleId,
        isRetail: domain && isRetail(domain),
        playerConfigData,
      } as MultiplePlaybackData)) ||
    undefined
)

export const trailerComponentSelector = createSelector(
  [
    sessionIdSelector,
    simpleSelectors.pageTitleId,
    playerConfigDataSelector,
    pageSelectors.trailer,
  ],
  (
    sessionId: string,
    pageTitleId: string,
    playerConfigData: playerConfig.PlayerConfigData,
    trailerFragment?: WpsRuntime.atv.wps.TrailerFragmentModelProps
  ) =>
    (trailerFragment && {
      playbackData: trailerFragment,
      playerConfigData,
      sessionId,
      pageTitleId,
    }) ||
    undefined
)

export const purchaseComponentSelector = createSelector(
  [acquisitionActionsSelector, pageSelectors.buyboxTitleId],
  (
    acquisitionActions?: WpsRuntime.atv.wps.AcquisitionActionsModelProps,
    titleId?: string
  ) =>
    acquisitionActions && {
      acquisitionActions,
      titleId,
      forceDoubleHeight: true,
    }
)

export const disneyComponentSelector = createSelector(
  disneyActionSelector,
  (disneyAction?: WpsRuntime.atv.wps.DisneyActionProps) =>
    disneyAction && {
      message: disneyAction.message,
      returnLink: disneyAction.returnLink,
    }
)

export const shouldShowMwtwSelector = createSelector(
  mwtwSelector,
  (moreWaysToWatch?: WpsRuntime.atv.wps.AcquisitionActionTreeModelProps) => {
    if (!moreWaysToWatch) {
      return false as boolean
    }

    return Boolean(
      (
        (moreWaysToWatch as
          | WpsRuntime.atv.wps.AcquisitionActionTreeRightProps
          | WpsRuntime.atv.wps.AcquisitionActionTreeLeftProps).children || []
      ).length > 0
    )
  }
)

export const mwtwComponentSelector = createSelector(
  [shouldShowMwtwSelector, mwtwSelector, pageSelectors.buyboxTitleId],
  (
    shouldShowMoreWaysToWatch: boolean,
    moreWaysToWatch?: WpsRuntime.atv.wps.AcquisitionActionTreeModelProps,
    titleId?: string
  ) => ({
    shouldShowMoreWaysToWatch,
    moreWaysToWatch,
    titleId,
  })
)

export const redeemAndPromoComponentSelector = createSelector(
  [
    isIosMShopSelector,
    sanitizedRequestContextSelector,
    simpleSelectors.pageTitleId,
    mwtwSelector,
    tvodWinnerChildrenSelector,
  ],
  (
    isRedeemAndPromoRestricted: boolean,
    requestContext: IRequestContext,
    titleID: string,
    moreWaysToWatch?: WpsRuntime.atv.wps.AcquisitionActionTreeModelProps,
    tvodWinnersChildren?: WpsRuntime.atv.wps.PurchaseAction[],
  ) => ({
    isRedeemAndPromoRestricted,
    requestContext,
    titleID,
    moreWaysToWatch,
    hasTvodWinnerOffer: Boolean(
      tvodWinnersChildren && tvodWinnersChildren.length > 0
    ),
  })
)

export const secondaryOfferMessageComponentSelector = createSelector(
  [svodMetadataSelector, tvodMetadataSelector],
  (
    svodMetadata?: WpsRuntime.atv.wps.ActionMetadataProps,
    tvodMetadata?: WpsRuntime.atv.wps.ActionMetadataProps
  ) => {
    const svodSecondaryMessages =
      (svodMetadata && svodMetadata.secondaryMessages) || []
    const tvodSecondaryMessages =
      (tvodMetadata && tvodMetadata.secondaryMessages) || []

    return { messages: [...svodSecondaryMessages, ...tvodSecondaryMessages] }
  }
)

export const comingSoonMessagesPresentSelector = createSelector(
  [
    notificationActionComingSoonMessagesSelector,
    notificationAlertsSelector
  ],
  (
    notificationComingSoonMessages: WpsRuntime.atv.wps.DVStringList,
    notificationAlerts?:  WpsRuntime.atv.wps.AlertNotification[],
  ) => {
    return (
      (notificationAlerts && notificationAlerts.some(alert => alert.notificationType === 'ComingSoon')) || (notificationComingSoonMessages.length > 0)
    )
  }
)